import { Pipe, PipeTransform } from '@angular/core';

interface CountryFormat {
  code: string; // dialing code with + (e.g. '+92')
  nationalLen: number; // expected number of digits after the code
  groups: number[]; // grouping pattern (e.g. [3, 3, 4])
}

const COUNTRIES: CountryFormat[] = [
  { code: '+64', nationalLen: 9, groups: [2, 3, 4] }, // New Zealand
  { code: '+44', nationalLen: 10, groups: [4, 6] }, // United Kingdom
  { code: '+92', nationalLen: 10, groups: [3, 3, 4] }, // Pakistan
  { code: '+1', nationalLen: 10, groups: [3, 3, 4] }, // US / Canada
];

// AU isn't in COUNTRIES because we render it as a local 0XXX XXX XXX number,
// but we still want to detect it when entered without the '+'.
const AU_CC = '61';
const AU_TOTAL_LEN = 11; // 61 + 9 national digits

@Pipe({ name: 'mobileFormat' })
export class MobileFormatPipe implements PipeTransform {
  transform(value: string): string {
    if (!value) return '';

    let raw = value.toString();
    let compact = raw.replace(/\s/g, '');

    // --- NEW: auto-detect international numbers missing the leading '+' ---
    if (!compact.startsWith('+')) {
      const digitsOnly = compact.replace(/\D/g, '');

      // AU without '+': e.g. "61412633986"
      if (digitsOnly.startsWith(AU_CC) && digitsOnly.length === AU_TOTAL_LEN) {
        raw = '+' + digitsOnly;
        compact = raw;
      } else {
        // Other known country codes without '+'
        const match = COUNTRIES.find(c => {
          const ccDigits = c.code.substring(1); // strip '+'
          return digitsOnly.startsWith(ccDigits) && digitsOnly.length === ccDigits.length + c.nationalLen;
        });
        if (match) {
          raw = '+' + digitsOnly;
          compact = raw;
        }
      }
    }
    // ----------------------------------------------------------------------

    // International number (starts with +)
    if (raw.trim().startsWith('+')) {
      // Australia (+61) → convert to local 0XXX XXX XXX
      if (compact.startsWith('+61')) {
        let digits = raw.replace(/\D/g, '').substring(2);
        if (!digits.startsWith('0')) digits = '0' + digits;
        return this.formatGroups(digits.substring(0, 10), [4, 3, 3]);
      }

      // Other known countries
      const country = COUNTRIES.find(c => compact.startsWith(c.code));
      if (country) {
        const ccLen = country.code.length - 1; // digits in country code
        const digits = raw.replace(/\D/g, '').substring(ccLen, ccLen + country.nationalLen);
        if (digits.length === 0) return country.code;
        return `${country.code} ${this.formatGroups(digits, country.groups)}`;
      }

      // Unknown country code → free text
      return raw.replace(/[^\d+\s]/g, '');
    }

    // No + and not detected as international → treat as local AU number
    const digits = raw.replace(/\D/g, '').substring(0, 10);
    return this.formatGroups(digits, [4, 3, 3]);
  }

  private formatGroups(digits: string, groups: number[]): string {
    const parts: string[] = [];
    let i = 0;
    for (const g of groups) {
      if (i >= digits.length) break;
      parts.push(digits.substring(i, i + g));
      i += g;
    }
    return parts.join(' ');
  }
}
